import { Mark, mergeAttributes } from '@tiptap/core';
import { createPopoverPlugin } from './popover-plugin';
import { generatePopoverId, PopoverAttributes } from './popover.model';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    popover: {
      /**
       * Set popover mark with content and optional link
       */
      setPopover: (attributes: PopoverAttributes) => ReturnType;
      /**
       * Remove popover mark
       */
      unsetPopover: () => ReturnType;
      /**
       * Toggle popover mark
       */
      togglePopover: (attributes: PopoverAttributes) => ReturnType;
      /**
       * Update popover mark attributes by ID
       */
      updatePopoverById: (id: string, attributes: Partial<PopoverAttributes>) => ReturnType;
    };
  }
}

export interface PopoverOptions {
  HTMLAttributes: Record<string, string>;
}

export const Popover = Mark.create<PopoverOptions>({
  name: 'popover',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-popover-id'),
        renderHTML: (attributes) => {
          if (!attributes['id']) {
            return {};
          }
          return {
            'data-popover-id': attributes['id'],
          };
        },
      },
      htmlContent: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-html-content'),
        renderHTML: (attributes) => {
          if (!attributes['htmlContent']) {
            return {};
          }
          return {
            'data-html-content': attributes['htmlContent'],
          };
        },
      },
      linkUrl: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-link-url'),
        renderHTML: (attributes) => {
          if (!attributes['linkUrl']) {
            return {};
          }
          return {
            'data-link-url': attributes['linkUrl'],
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-popover]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-popover': '',
        class: 'popover-mark',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setPopover:
        (attributes) =>
        ({ commands }) => {
          // Auto-generate ID if not provided
          const attrsWithId = {
            ...attributes,
            id: attributes.id || generatePopoverId(),
          };
          return commands.setMark(this.name, attrsWithId);
        },
      unsetPopover:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
      togglePopover:
        (attributes) =>
        ({ commands }) => {
          // Auto-generate ID if not provided
          const attrsWithId = {
            ...attributes,
            id: attributes.id || generatePopoverId(),
          };
          return commands.toggleMark(this.name, attrsWithId);
        },
      updatePopoverById:
        (id, attributes) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          let updated = false;

          doc.descendants((node, pos) => {
            if (node.isText) {
              const marks = node.marks.filter((mark) => mark.type.name === 'popover' && mark.attrs['id'] === id);
              if (marks.length > 0) {
                const mark = marks[0];
                const newMark = mark.type.create({ ...mark.attrs, ...attributes });
                tr.addMark(pos, pos + node.nodeSize, newMark);
                updated = true;
              }
            }
          });

          if (updated && dispatch) {
            dispatch(tr);
          }

          return updated;
        },
    };
  },

  addProseMirrorPlugins() {
    return [createPopoverPlugin(this.editor)];
  },
});
